import React, {Suspense, useCallback} from 'react';

import {SvgIcon} from '@mui/material';
import {Person as PersonIcon} from '@mui/icons-material';
import {useSnackbar, withSnackbar} from 'notistack';
import {
  createBrowserRouter,
  createRoutesFromElements,
  LoaderFunctionArgs,
  Navigate,
  Route,
  RouterProvider,
} from 'react-router-dom';
import {useMutation, useQueryClient} from 'react-query';

import API, {Types, PageViewEvent, useAuth, useCsrfToken} from '@wheeebo/polaris-api';
import {DroneIcon} from '@wheeebo/polaris-svg';
import {config} from '@components/Constants';
import {
  BreadcrumbItem,
  CircleLoader,
  EmailVerifyPage,
  EmailUnverifiedPage,
  ErrorBoundary,
  ForgotPasswordPage,
  LoadError,
  LoginPage,
  Head,
  PasswordResetPage,
  SignUpPage,
} from '@wheeebo/polaris-ui';

import GuestPageFrame from '@components/pages/GuestPageFrame';
import AdminPageFrame from '@components/pages/AdminPageFrame';
import MemberPage from '@components/pages/MemberPage';
import MemberListPage from '@components/pages/MemberListPage';
import VehiclePage from '@components/pages/VehiclePage';
import VehicleListPage from '@components/pages/VehicleListPage';
import VehicleRegisterPage from '@components/pages/VehicleRegisterPage';

const MemberBreadcrumb: BreadcrumbItem = {
  label: 'Members',
  link: '/members',
  icon: <PersonIcon fontSize="small" />,
};
const VehicleBreadcrumb: BreadcrumbItem = {
  label: 'Vehicles',
  link: '/vehicles',
  icon: (
    <SvgIcon fontSize="small">
      <DroneIcon />
    </SvgIcon>
  ),
};

function Navigator(): JSX.Element {
  const {csrfToken, error: csrfTokenError} = useCsrfToken();
  const {authState, account, isAccountLoading, signInWithGoogle} = useAuth();
  const {enqueueSnackbar} = useSnackbar();
  const handleGoogleSignIn = useCallback(
    async (token: string): Promise<void> => {
      if (csrfToken === undefined) {
        enqueueSnackbar('サインインの準備中です。少し待ってからもう一度お試しください。', {variant: 'warning'});
        return;
      }
      try {
        await signInWithGoogle(token, csrfToken);
      } catch (e) {
        enqueueSnackbar(e instanceof Error ? e.message : 'Google認証に失敗しました。もう一度お試しください。', {
          variant: 'error',
        });
      }
    },
    [csrfToken, signInWithGoogle, enqueueSnackbar],
  );
  const {mutate: emitPageViewEvent} = useMutation<unknown, unknown, PageViewEvent>((event: PageViewEvent) =>
    API.Track.emitPageViewEvent(csrfToken, event),
  );

  React.useEffect(
    () => emitPageViewEvent({path: location.pathname, search: location.search, hash: location.hash}),
    [emitPageViewEvent],
  );

  const locationState = React.useMemo(
    () => ({pathname: location.pathname, search: location.search, hash: location.hash}),
    [],
  );

  const queryClient = useQueryClient();
  const fetchMember = async ({params}: LoaderFunctionArgs): Promise<Types.Member> =>
    await queryClient.fetchQuery(params.memberId ? API.Members.getKey(params.memberId) : '', () =>
      params.memberId ? API.Members.get(params.memberId) : undefined,
    );
  const fetchVehicle = async ({params}: LoaderFunctionArgs): Promise<Types.Vehicle> =>
    await queryClient.fetchQuery(params.vehicleId ? API.Vehicles.getKey(params.vehicleId) : '', () =>
      params.vehicleId ? API.Vehicles.get(params.vehicleId, {}) : undefined,
    );
  const guestRoute = createRoutesFromElements(
    <Route errorElement={<ErrorBoundary />}>
      <Route path="/" element={<GuestPageFrame />}>
        <Route
          index
          element={<LoginPage disableSignup={false} googleClientId={config.GOOGLE_CLIENT_ID} onGoogleSignIn={handleGoogleSignIn} />}
        />
        <Route path="signup" element={<SignUpPage />} />
        <Route path="forgot_password" element={<ForgotPasswordPage />} />
        <Route path="password_reset" element={<PasswordResetPage />} />
        <Route path="email_verify" element={<EmailVerifyPage />} />
        <Route path="*" element={<Navigate to="/" state={{from: locationState}} />} />
      </Route>
      <Route path="*" element={<Navigate to="/" state={{from: locationState}} />} />
    </Route>,
  );
  const EmailUnverifiedRoute = createRoutesFromElements(
    <Route errorElement={<ErrorBoundary />}>
      <Route path="/" element={<GuestPageFrame />}>
        <Route index element={<Navigate to="/email_unverified" state={{from: locationState}} />} />
        <Route path="email_unverified" element={<EmailUnverifiedPage />} />
        <Route path="email_verify" element={<EmailVerifyPage />} />
        <Route path="*" element={<Navigate to="/" state={{from: locationState}} />} />
      </Route>
      <Route path="*" element={<Navigate to="/" state={{from: locationState}} />} />
    </Route>,
  );
  const adminRoute = createRoutesFromElements(
    <Route errorElement={<ErrorBoundary />}>
      <Route path="" element={<AdminPageFrame />}>
        <Route path="members">
          <Route index element={<MemberListPage />} handle={{crumb: () => [MemberBreadcrumb]}} />
          <Route
            path=":memberId"
            element={<MemberPage />}
            loader={fetchMember}
            handle={{crumb: (data: Types.Member) => [MemberBreadcrumb, {label: data?.name || ' - '}]}}
          />
        </Route>
        <Route path="vehicles">
          <Route index element={<VehicleListPage />} handle={{crumb: () => [VehicleBreadcrumb]}} />
          <Route
            path="new"
            element={<VehicleRegisterPage />}
            handle={{crumb: () => [VehicleBreadcrumb, {label: 'New'}]}}
          />
          <Route
            path=":vehicleId"
            element={<VehiclePage />}
            loader={fetchVehicle}
            handle={{crumb: (data: Types.Vehicle) => [VehicleBreadcrumb, {label: data?.name || ' - '}]}}
          />
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="" state={{from: locationState}} />} />
    </Route>,
  );
  return (
    <Suspense fallback={<CircleLoader />}>
      <Head />
      {csrfTokenError ? (
        <LoadError />
      ) : authState.isLoading || isAccountLoading ? (
        <CircleLoader />
      ) : !account ? (
        <RouterProvider router={createBrowserRouter(guestRoute)} />
      ) : !account.emailVerified ? (
        <RouterProvider router={createBrowserRouter(EmailUnverifiedRoute)} />
      ) : (
        <RouterProvider router={createBrowserRouter(adminRoute)} />
      )}
    </Suspense>
  );
}

export default React.memo(withSnackbar(Navigator));
